// src/pages/messages/MessagesListPage.tsx

import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Search,
  Send,
  Smartphone,
  User,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchApp } from "../../services/apiService";

interface MessageItem {
  id: string | number;
  customer_id: string | number | null;
  phone_number: string;
  channel: "sms" | "whatsapp" | string;
  message: string;
  delivered: boolean;
  status: string;
  created_at: string;
  sent_at?: string | null;
}

type MessageTab = "all" | "sms" | "whatsapp";

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const normalizeList = <T,>(res: any): T[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getMessageDate = (message: MessageItem) =>
  message.sent_at || message.created_at;

const getStatusLabel = (status: string, delivered: boolean) => {
  const s = String(status || "").toLowerCase();

  if (s === "failed") return "Failed";
  if (delivered || s === "delivered") return "Delivered";
  if (s === "sent") return "Sent";
  return "Queued";
};

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
  if (channel === "whatsapp") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <MessageCircle className="h-3 w-3" />
        WhatsApp
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
      <Smartphone className="h-3 w-3" />
      SMS
    </span>
  );
};

const StatusBadge: React.FC<{ status: string; delivered: boolean }> = ({
  status,
  delivered,
}) => {
  const s = String(status || "").toLowerCase();

  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[11px] font-black text-rose-700">
        <XCircle className="h-3 w-3" />
        Failed
      </span>
    );
  }

  if (delivered || s === "delivered") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        Delivered
      </span>
    );
  }

  if (s === "sent") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
        <Send className="h-3 w-3" />
        Sent
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-800">
      <Clock className="h-3 w-3" />
      Queued
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 2
              ? "w-72"
              : index === 4 || index === 5
                ? "w-24 rounded-full"
                : "w-28"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 h-16 rounded-2xl bg-slate-200" />
  </div>
);

const tabs: Array<{ key: MessageTab; label: string }> = [
  { key: "all", label: "All" },
  { key: "sms", label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
];

const MessagesListPage: React.FC = () => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] = useState<MessageTab>("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [error, setError] = useState<string | null>(null);

  const loadMessages = async (manual = false) => {
    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const res = await fetchApp("/messages?limit=200&offset=0");
      setMessages(normalizeList<MessageItem>(res));
    } catch (err: any) {
      setError(err?.message || "Failed to load messages.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMessages(false);
  }, []);

  const filteredMessages = useMemo(() => {
    const q = search.trim().toLowerCase();

    return messages.filter((message) => {
      if (activeTab !== "all" && message.channel !== activeTab) return false;

      const messageDate = getMessageDate(message);
      const dateValue = messageDate ? new Date(messageDate) : null;

      if (startDate && dateValue) {
        const start = new Date(`${startDate}T00:00:00`);
        if (dateValue < start) return false;
      }

      if (endDate && dateValue) {
        const end = new Date(`${endDate}T23:59:59`);
        if (dateValue > end) return false;
      }

      if (!q) return true;

      return [
        message.phone_number,
        message.message,
        message.channel,
        message.status,
        String(message.customer_id || ""),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [messages, activeTab, search, startDate, endDate]);

  const stats = useMemo(() => {
    const total = filteredMessages.length;
    const delivered = filteredMessages.filter((msg) => msg.delivered).length;
    const failed = filteredMessages.filter(
      (msg) => String(msg.status || "").toLowerCase() === "failed",
    ).length;
    const sms = filteredMessages.filter((msg) => msg.channel === "sms").length;
    const whatsapp = filteredMessages.filter(
      (msg) => msg.channel === "whatsapp",
    ).length;

    return {
      total,
      delivered,
      failed,
      sms,
      whatsapp,
    };
  }, [filteredMessages]);

  const openCustomerMessages = (
    customerId: string | number | null,
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation();

    if (!customerId) return;

    navigate(`/messages/customer/${customerId}`);
  };

  const clearFilters = () => {
    setSearch("");
    setActiveTab("all");
    setStartDate("");
    setEndDate("");
  };

  const hasFilters = search || activeTab !== "all" || startDate || endDate;

  if (loading && messages.length === 0) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            <div className="relative overflow-hidden px-6 py-10 text-center">
              <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
              <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

              <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              </div>

              <p className="relative mt-4 text-[15px] font-black text-slate-900">
                Loading messages…
              </p>

              <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
                Please wait while message records are being prepared.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Messaging
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Messages
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Review all SMS and WhatsApp message logs, delivery status,
                  customer phone numbers and full message content.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => loadMessages(true)}
                  disabled={refreshing}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/messages/send")}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  New Message
                </button>
              </div>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing messages…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            sub="Filtered messages"
            icon={<MessageCircle className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Delivered"
            value={stats.delivered}
            sub="Successful messages"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Failed"
            value={stats.failed}
            sub="Failed messages"
            icon={<XCircle className="h-5 w-5" />}
            tone={stats.failed > 0 ? "rose" : "slate"}
          />

          <StatCard
            label="SMS"
            value={stats.sms}
            sub="Text messages"
            icon={<Smartphone className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="WhatsApp"
            value={stats.whatsapp}
            sub="WhatsApp messages"
            icon={<MessageCircle className="h-5 w-5" />}
            tone="emerald"
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[14px] font-black text-slate-900">
                Message Logs
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Showing {filteredMessages.length} of {messages.length} messages
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:flex xl:items-center">
              <div className="relative w-full xl:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search phone, message, status..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="flex overflow-hidden rounded-2xl border border-slate-300 bg-slate-50 p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`h-8 rounded-xl px-3 text-[12px] font-black transition ${
                      activeTab === tab.key
                        ? "bg-emerald-500 text-white shadow-sm"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="relative w-full xl:w-44">
                <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`${inputCls} pl-10`}
                />
              </div>

              <div className="relative w-full xl:w-44">
                <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`${inputCls} pl-10`}
                />
              </div>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Phone</th>
                  <th className="px-5 py-3.5 font-black">Message</th>
                  <th className="px-5 py-3.5 font-black">Channel</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 font-black">Date</th>
                  <th className="px-5 py-3.5 text-right font-black">View</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredMessages.length > 0 ? (
                  filteredMessages.map((message) => (
                    <tr
                      key={String(message.id)}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <User className="h-4 w-4" />
                          </div>

                          <div>
                            <p className="text-[13px] font-black text-slate-900">
                              Customer
                            </p>

                            {/* <p className="mt-0.5 font-mono text-[11px] font-bold text-slate-500">
                              {message.customer_id || "No customer ID"}
                            </p> */}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-700">
                        {message.phone_number || "—"}
                      </td>

                      <td className="max-w-[420px] px-5 py-4">
                        <p
                          className="line-clamp-2 text-[12.5px] font-medium leading-relaxed text-slate-700"
                          title={message.message}
                        >
                          {message.message || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <ChannelBadge channel={message.channel} />
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge
                          status={message.status}
                          delivered={message.delivered}
                        />
                      </td>

                      <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                        {formatDateTime(getMessageDate(message))}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          disabled={!message.customer_id}
                          onClick={(e) =>
                            openCustomerMessages(message.customer_id, e)
                          }
                          className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Show
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <MessageCircle className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No messages found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No message records match your current filters.
                      </p>

                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredMessages.length > 0 ? (
              filteredMessages.map((message) => (
                <div key={String(message.id)} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-[12.5px] font-black text-slate-900">
                        {message.phone_number || "—"}
                      </p>

                      <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                        {formatDateTime(getMessageDate(message))}
                      </p>
                    </div>

                    <StatusBadge
                      status={message.status}
                      delivered={message.delivered}
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <ChannelBadge channel={message.channel} />

                    <span className="inline-flex rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      {getStatusLabel(message.status, message.delivered)}
                    </span>
                  </div>

                  <div className="mt-3 rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                    <p className="line-clamp-4 text-[13px] font-medium leading-relaxed text-slate-800">
                      {message.message || "—"}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={!message.customer_id}
                    onClick={(e) =>
                      openCustomerMessages(message.customer_id, e)
                    }
                    className={`${btnBase} mt-3 h-10 w-full border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400`}
                  >
                    <Eye className="h-4 w-4" />
                    Show Customer Messages
                  </button>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <MessageCircle className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No messages found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No message records match your current filters.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredMessages.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {messages.length}
              </span>{" "}
              messages
            </p>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesListPage;
